import React, { useEffect, useState } from 'react';
import emailService from '../services/emailService';

function WhatsAppList() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [query, setQuery] = useState('');
  
  // Nouveaux états pour les filtres
  const [availableDates, setAvailableDates] = useState([]);
  const [availableConversations, setAvailableConversations] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedConversation, setSelectedConversation] = useState('');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' ou 'desc'
  const [viewMode, setViewMode] = useState('list'); // 'list' ou 'chat'
  const [exportLoading, setExportLoading] = useState({
    csv: false,
    pdf: false,
    all: false
  });

  // Charger les données initiales
  useEffect(() => {
    loadFilters();
    load(1);
  }, []);

  // Recharger quand les filtres changent
  useEffect(() => {
    load(1);
  }, [selectedDate, selectedConversation, sortOrder]);

  const loadFilters = async () => {
    try {
      // Charger les dates disponibles
      const datesData = await emailService.getWhatsAppDates();
      setAvailableDates(datesData.dates || []);
      
      // Charger les conversations disponibles
      const conversationsData = await emailService.getWhatsAppConversations();
      setAvailableConversations(conversationsData.conversations || []);
    } catch (e) {
      console.error('Erreur chargement filtres:', e);
    }
  };

  const load = async (p = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      let data;
      if (query.trim()) {
        // Mode recherche - on garde le filtrage côté client pour la recherche
        data = await emailService.searchWhatsApp(query);
        let searchResults = data.messages || [];
        
        // Appliquer les filtres même en mode recherche
        if (selectedDate) {
          searchResults = searchResults.filter(m => {
            if (!m.date) return false;
            const messageDate = new Date(m.date).toISOString().split('T')[0];
            return messageDate === selectedDate;
          });
        }
        
        if (selectedConversation) {
          searchResults = searchResults.filter(m =>
            m.conversation === selectedConversation
          );
        }
        
        // Tri
        searchResults.sort((a, b) => {
          const dateA = new Date(a.date || 0);
          const dateB = new Date(b.date || 0);
          return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
        });
        
        setMessages(searchResults);
        setTotalPages(1);
      } else {
        // Mode liste normale avec filtres backend
        const filters = {
          date_filter: selectedDate || undefined,
          conversation_filter: selectedConversation || undefined,
          sort_order: sortOrder
        };
        
        data = await emailService.listWhatsAppMessages(p, 50, filters); // Plus de messages pour le chat
        setMessages(data.messages || []);
        setTotalPages(data.pagination?.pages || 1);
      }
    } catch (e) {
      setError(e.message);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const onSearch = async (e) => {
    e.preventDefault();
    load(1); // Utiliser la fonction load unifiée
  };

  const clearFilters = () => {
    setSelectedDate('');
    setSelectedConversation('');
    setQuery('');
    setSortOrder('asc');
  };

  // Fonctions d'export
  const handleExportCSV = async (allMessages = false) => {
    const exportType = allMessages ? 'all' : 'csv';
    setExportLoading(prev => ({ ...prev, [exportType]: true }));
    
    try {
      const filters = {
        date_filter: selectedDate || undefined,
        conversation_filter: selectedConversation || undefined,
        sort_order: sortOrder
      };
      await emailService.exportWhatsAppCSV(query, allMessages, filters);
      // Notification de succès (optionnel)
    } catch (err) {
      alert(`Erreur lors de l'export CSV: ${err.message}`);
    } finally {
      setExportLoading(prev => ({ ...prev, [exportType]: false }));
    }
  };

  const handleExportPDF = async (allMessages = false) => {
    const exportType = allMessages ? 'all' : 'pdf';
    setExportLoading(prev => ({ ...prev, [exportType]: true }));
    
    try {
      const filters = {
        date_filter: selectedDate || undefined,
        conversation_filter: selectedConversation || undefined,
        sort_order: sortOrder
      };
      await emailService.exportWhatsAppPDF(query, allMessages, filters);
      // Notification de succès (optionnel)
    } catch (err) {
      alert(`Erreur lors de l'export PDF: ${err.message}`);
    } finally {
      setExportLoading(prev => ({ ...prev, [exportType]: false }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateForSelect = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Grouper les messages par conversation pour le mode chat
  const groupedMessages = React.useMemo(() => {
    if (viewMode !== 'chat') return {};
    
    const groups = {};
    messages.forEach(message => {
      const conv = message.conversation || 'Conversation inconnue';
      if (!groups[conv]) {
        groups[conv] = [];
      }
      groups[conv].push(message);
    });
    
    // Trier chaque groupe par date
    Object.keys(groups).forEach(conv => {
      groups[conv].sort((a, b) => {
        const dateA = new Date(a.date || 0);
        const dateB = new Date(b.date || 0);
        return dateA - dateB; // Toujours chronologique pour le chat
      });
    });
    
    return groups;
  }, [messages, viewMode]);

  // Obtenir une couleur unique pour chaque expéditeur
  const getSenderColor = (sender) => {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500',
      'bg-indigo-500', 'bg-red-500', 'bg-yellow-500', 'bg-teal-500'
    ];
    let hash = 0;
    for (let i = 0; i < sender.length; i++) {
      hash = sender.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {/* En-tête avec titre et contrôles */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Messages WhatsApp</h1>
        
        {/* Contrôles de vue */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'list' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            📋 Liste
          </button>
          <button
            onClick={() => setViewMode('chat')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'chat' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            💬 Chat
          </button>
        </div>
      </div>

      {/* Barre de recherche */}
      <form onSubmit={onSearch} className="mb-4">
        <div className="flex gap-2">
          <input 
            className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
            placeholder="Rechercher dans les messages..." 
            value={query} 
            onChange={(e) => setQuery(e.target.value)} 
          />
          <button 
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            🔍 Rechercher
          </button>
        </div>
      </form>

      {/* Filtres */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Filtre par date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              📅 Filtrer par date
            </label>
            <select
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Toutes les dates</option>
              {availableDates.map(date => (
                <option key={date} value={date}>
                  {formatDateForSelect(date)}
                </option>
              ))}
            </select>
          </div>

          {/* Filtre par conversation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              💬 Filtrer par conversation
            </label>
            <select
              value={selectedConversation}
              onChange={(e) => setSelectedConversation(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Toutes les conversations</option>
              {availableConversations.map(conv => (
                <option key={conv.conversation} value={conv.conversation}>
                  {conv.conversation} ({conv.count} messages)
                </option>
              ))}
            </select>
          </div>

          {/* Tri par date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              🔄 Tri par date
            </label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="asc">Plus ancien en premier</option>
              <option value="desc">Plus récent en premier</option>
            </select>
          </div>

          {/* Bouton reset */}
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              🗑️ Effacer filtres
            </button>
          </div>
        </div>
      </div>

      {/* Indicateurs de filtres actifs */}
      {(selectedDate || selectedConversation || query) && (
        <div className="mb-4 flex flex-wrap gap-2">
          {selectedDate && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
              📅 {formatDateForSelect(selectedDate)}
              <button 
                onClick={() => setSelectedDate('')}
                className="ml-2 text-blue-600 hover:text-blue-800"
              >
                ×
              </button>
            </span>
          )}
          {selectedConversation && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
              💬 {selectedConversation}
              <button 
                onClick={() => setSelectedConversation('')}
                className="ml-2 text-green-600 hover:text-green-800"
              >
                ×
              </button>
            </span>
          )}
          {query && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
              🔍 "{query}"
              <button 
                onClick={() => setQuery('')}
                className="ml-2 text-purple-600 hover:text-purple-800"
              >
                ×
              </button>
            </span>
          )}
        </div>
      )}

      {/* Export Buttons */}
      {!loading && messages.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            <button
              className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-50"
              onClick={() => handleExportCSV(false)}
              disabled={exportLoading.csv}
            >
              {exportLoading.csv ? (
                <>
                  <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                  Export CSV...
                </>
              ) : (
                <>
                  📊 Export CSV ({query ? 'résultats' : 'page courante'})
                </>
              )}
            </button>
            
            <button
              className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm disabled:opacity-50"
              onClick={() => handleExportPDF(false)}
              disabled={exportLoading.pdf}
            >
              {exportLoading.pdf ? (
                <>
                  <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                  Export PDF...
                </>
              ) : (
                <>
                  📄 Export PDF ({query ? 'résultats' : 'page courante'})
                </>
              )}
            </button>
            
            <button
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50"
              onClick={() => {
                if (window.confirm('Exporter tous les messages WhatsApp peut prendre du temps. Continuer ?')) {
                  handleExportCSV(true);
                }
              }}
              disabled={exportLoading.all}
            >
              {exportLoading.all ? (
                <>
                  <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                  Export en cours...
                </>
              ) : (
                <>
                  💾 Exporter tous les messages (CSV)
                </>
              )}
            </button>
          </div>
          
          <div className="mt-2">
            <small className="text-gray-600">
              ℹ️ {query
                ? `Export des ${messages.length} messages trouvés pour "${query}"`
                : `Export de la page courante (${messages.length} messages)`
              }
              {selectedDate && ` • Filtrés par date: ${formatDateForSelect(selectedDate)}`}
              {selectedConversation && ` • Conversation: ${selectedConversation}`}
            </small>
          </div>
        </div>
      )}

      {/* États de chargement et erreur */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Chargement...</p>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-red-600">❌ {String(error)}</p>
        </div>
      )}
      
      {!loading && messages.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">📭 Aucun message trouvé.</p>
        </div>
      )}

      {/* Affichage des messages */}
      {!loading && messages.length > 0 && (
        <>
          {viewMode === 'list' ? (
            /* Mode Liste */
            <div className="space-y-3">
              {messages.map(m => (
                <a 
                  key={m.id} 
                  href={`/whatsapp/${m.id}`} 
                  className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                        <span className="font-medium">📅 {formatDate(m.date)}</span>
                        <span>•</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSenderColor(m.sender)} text-white`}>
                          {m.sender}
                        </span>
                      </div>
                      <div className="font-medium text-gray-800 mb-1">
                        💬 {m.conversation}
                      </div>
                      <div className="text-gray-700 line-clamp-2">
                        {m.content}
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            /* Mode Chat */
            <div className="space-y-6">
              {Object.entries(groupedMessages).map(([conversation, msgs]) => (
                <div key={conversation} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  {/* En-tête de conversation */}
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-800">💬 {conversation}</h3>
                    <p className="text-sm text-gray-600">{msgs.length} messages</p>
                  </div>
                  
                  {/* Messages de la conversation */}
                  <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                    {msgs.map((msg, index) => {
                      const isFirstFromSender = index === 0 || msgs[index - 1].sender !== msg.sender;
                      const senderColor = getSenderColor(msg.sender);
                      
                      return (
                        <div key={msg.id} className="flex items-start gap-3">
                          {/* Avatar */}
                          {isFirstFromSender && (
                            <div className={`w-8 h-8 rounded-full ${senderColor} flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>
                              {msg.sender.charAt(0).toUpperCase()}
                            </div>
                          )}
                          {!isFirstFromSender && <div className="w-8"></div>}
                          
                          {/* Bulle de message */}
                          <div className="flex-1">
                            {isFirstFromSender && (
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-medium text-gray-700">
                                  {msg.sender}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {formatDate(msg.date)}
                                </span>
                              </div>
                            )}
                            <div className="bg-gray-100 rounded-lg px-3 py-2 max-w-md">
                              <p className="text-gray-800 whitespace-pre-wrap text-sm">
                                {msg.content}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination (seulement en mode liste) */}
          {viewMode === 'list' && !query && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button 
                disabled={page <= 1} 
                onClick={() => {
                  const newPage = Math.max(1, page - 1);
                  setPage(newPage);
                  load(newPage);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                ← Précédent
              </button>
              <span className="px-4 py-2 text-gray-600">
                Page {page} / {totalPages}
              </span>
              <button 
                disabled={page >= totalPages} 
                onClick={() => {
                  const newPage = Math.min(totalPages, page + 1);
                  setPage(newPage);
                  load(newPage);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                Suivant →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default WhatsAppList;
